// DEPENDENCIES
import { FormikValues } from "formik";

// TYPES
import { Template, PersonType, DraftUUID, ComponentStep } from "../../types";
import { AppDispatch, RootState } from "../../app/store";

// UTILS
import { checkboxRequired, checkbox } from "../../validations";
import { getDraftStepsToSend, handleSendStep } from "../../app/utils";
import { onUpdateFormData } from "../../reducers/onboardingDataReducer";
import { getCompanyData, isAgrofinanzasOnboarding } from "../../utils";
import getTemplateData from "../../hooks/useTemplate";

// STEPS
import infoStep from "../../steps/physical/infoStep";
import tokenStep from "../../steps/physical/tokenStep";
import identityStep from "../../steps/physical/identityStep";
import basicInformationStep from "../../steps/physical/basicInformationStep";
import preconfirmationSteps from "../../steps/physical/preconfirmationSteps";
import employmentProfileStep from "../../steps/physical/employmentProfileStep";
import investmentProfileStep from "../../steps/physical/investmentProfileStep";
import personalInformationStep from "../../steps/physical/personalInformationStep";

import bodyIcon from "../../assets/images/icons/ui/body.svg";
import { showOnlyInDev } from "../../constants";

const onboardingPhysical = async (
  draftUUID: DraftUUID,
  dispatch: AppDispatch,
  formValues?: FormikValues | null
): Promise<Template> => {
  try {
    const {
      favicon,
      formTitle,
      logo,
      questions,
      heritageDocuments,
      countries,
      countriesOptions,
      citizenshipOptions,
      activitiesOptions,
      getProvincesFilteredByCountry,
      provinces,
    } = await getTemplateData({
      draftUUID,
      formValues,
    });

    if (heritageDocuments) {
      dispatch(
        onUpdateFormData({
          heritageFiles: heritageDocuments,
        })
      );
    }

    const steps = [
      basicInformationStep(),
      tokenStep({ step: 2 }),
      identityStep(countries),
      personalInformationStep(
        countriesOptions,
        citizenshipOptions,
        getProvincesFilteredByCountry,
        provinces
      ),
      employmentProfileStep(
        activitiesOptions,
        countriesOptions,
        getProvincesFilteredByCountry,
        provinces
      ),
      investmentProfileStep(questions, heritageDocuments),
    ];

    return {
      onboardingType: "PHYSICAL",
      formTitle,
      logo,
      icon: bodyIcon,
      favicon,
      steps: [
        ...steps.map((step, index) => ({ step: index + 1, ...step })),
        infoStep,
      ],
      remoteSteps: [
        {
          link: "/dni",
          step: identityStep(countries).subSteps?.find(
            ({ label }) => label === "uploadDNI"
          ) as ComponentStep,
        },
        {
          link: "/identidad",
          step: identityStep(countries).subSteps?.find(
            ({ step }) => step === 3
          ) as ComponentStep,
        },
      ],
      preconfirmation: () => ({
        steps: preconfirmationSteps(
          countriesOptions,
          citizenshipOptions,
          activitiesOptions,
          getProvincesFilteredByCountry,
          provinces,
          questions
        ),
        checkboxes: getCompanyData(formValues).termsAndConditions.map(
          (term) => ({
            ...term,
            initialValue: false,
            validation: term.required ? checkboxRequired : checkbox,
          })
        ),
        sendTermsAndConditions: (state, values) =>
          handleSendTermsAndConditions(state, values, dispatch),
      }),
    };
  } catch (error) {
    throw new Error(error as string);
  }
};

const handleSendTermsAndConditions = async (
  state: RootState,
  values: FormikValues,
  dispatch: AppDispatch
) => {
  const {
    onboardingData: { sendedSteps, formValues },
    activeTemplate: { onboardingType },
  } = state;

  const termsAndConditionsStep = getPreconfirmationStep(values);
  await handleSendStep({
    dispatch,
    personType: onboardingType as PersonType,
    email: formValues?.email,
    sendedSteps,
    stepId: showOnlyInDev ? 5 : 4,
    step: termsAndConditionsStep,
  });

  if (showOnlyInDev) {
    const coownersOnboardingStep = {
      "@type": "6_PHYSICAL",
      coOwnerSteps: [],
    };

    await handleSendStep({
      personType: onboardingType as PersonType,
      email: formValues?.email,
      sendedSteps: getDraftStepsToSend(sendedSteps, termsAndConditionsStep),
      stepId: 6,
      step: coownersOnboardingStep,
      dispatch,
    });
  }
};

const getPreconfirmationStep = (values: FormikValues) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const step: any = {
    "@type": showOnlyInDev ? "5_PHYSICAL" : "4_PHYSICAL",
    acceptedOptionalTermsAndConditions: values?.senebiAndExteriorNotes,
    acceptedTermsAndConditions: values?.termsAndConditions,
  };

  if (isAgrofinanzasOnboarding()) {
    step.wantsAllariaAccount = !!values?.wantsAllariaAccount;
  }

  return step;
};

export default onboardingPhysical;
